<template>
  <v-flex xs12 class="location-targeting pb-2">
    <div v-if="isLoadingTargeting">
      <v-progress-circular
        :size="70"
        :width="4"
        color="primary"
        class="my-2 pb-2"
        indeterminate
      />
    </div>
    <div v-show="!isLoadingTargeting">
      <v-radio-group
        class="my-2 pb-2"
        hide-details
        v-model="targetingType"
        @change="updateLocationTargetingDetails"
        row
      >
        <v-radio
          :class="{ 'mb-2': $vuetify.breakpoint.xsOnly }"
          :label="$tc('message.bizgainOnboarding.advertiseNearYourBusiness')"
          color="primary"
          :value="targetType.latlng"
        />
        <v-radio
          :class="{ 'mb-2': $vuetify.breakpoint.xsOnly }"
          :label="$tc('message.bizgainOnboarding.advertiseInSpecificRegion')"
          color="primary"
          :value="targetType.region"
        />
      </v-radio-group>
      <v-card class="pa-3">
        <v-container class="pa-0 ma-0" fluid>
          <v-layout>
            <v-flex xs12 v-show="isLocationTargeting">
              <v-autocomplete
                v-model="location"
                :search-input.sync="search"
                :loading="isPlaceSuggestionsLoading"
                hide-no-data
                :items="searchResults"
                :placeholder="$tc('message.bizgainOnboarding.searchYourBusinessLocation')"
                @change="getPlaceDetails"
                :return-object="true"
                :open-on-clear="true"
                :no-filter="true"
                class="pt-0 mt-0 google-search"
                :menu-props="{ maxHeight: 170, contentClass: 'google-menu-content' }"
                append-icon="keyboard_arrow_down"
                prepend-inner-icon="search"
                hide-details
              >
                <template v-slot:selection="data">
                  <span class="text-ellipsis">
                    {{ data.item.shortText || data.item.text }}
                  </span>
                </template>
              </v-autocomplete>
              <div v-if="location && location.text" class="my-2 location-text">
                {{ location.text }}
              </div>
            </v-flex>
            <v-flex xs12 v-show="isRegionTargeting">
              <v-autocomplete
                v-model="specificLocation"
                :search-input.sync="searchSpecificLocation"
                :items="specificLocationResult"
                :placeholder="$tc('message.bizgainOnboarding.searchZipCodeOrCities')"
                :return-object="true"
                :open-on-clear="true"
                :no-filter="true"
                :loading="loadingSearchSpecificLocation"
                @change="getSelectedLocation"
                class="pt-0 mt-0"
                append-icon="keyboard_arrow_down"
                hide-details
                hide-no-data
                prepend-inner-icon="search"
              />
              <v-flex mt-2 xs12 v-if="selectedSpecificLocations && selectedSpecificLocations.length > 0">
                <v-layout
                  row
                  wrap
                >
                  <div v-for="(selectedSpecificLocation, index) in selectedSpecificLocations" :key="index">
                    <v-chip
                      class="selected-location-chips"
                    >
                      <span>
                        {{ selectedSpecificLocation && selectedSpecificLocation.text }}
                      </span>
                      <v-icon
                        small
                        right
                        color="black"
                        @click="removeSpecificLocation(index)"
                      >
                        close
                      </v-icon>
                    </v-chip>
                  </div>
                </v-layout>
              </v-flex>
            </v-flex>
          </v-layout>
          <v-layout>
            <v-flex>
              <v-layout mt-2 v-show="location && isLocationTargeting">
                <v-flex xs12>
                  <a @click="showLatLngMap = !showLatLngMap" class="map-header">{{ getMapHeader(showLatLngMap) }}</a>
                  <map-location
                    :search-location="search"
                    @searchResult="setSearchResult($event)"
                    @placeSuggestionsLoading="setPlaceSuggestionsLoading"
                    @updatePlaceDetails="updatePlaceDetails"
                    :selected-location="location"
                    ref="latlngMap"
                    map-id="map1"
                    :show-map="showLatLngMap"
                    :class="{ 'map': showLatLngMap }"
                  />
                </v-flex>
              </v-layout>
              <v-layout mt-2 v-show="isRegionTargeting && selectedSpecificLocations.length > 0">
                <v-flex xs12>
                  <a @click="showRegionMap = !showRegionMap" class="map-header">{{ getMapHeader(showRegionMap) }}</a>
                  <map-location
                    :search-location="specificLocationGMapSearch"
                    @searchResult="setSearchResult($event)"
                    @updatePlaceDetails="updatePlaceDetails"
                    :selected-location="selectedGMapSpecificLocation"
                    ref="region"
                    map-id="map2"
                    :show-map="showRegionMap"
                    :class="{ 'map': showRegionMap }"
                    :region-specific-locations="selectedSpecificLocations"
                  />
                </v-flex>
              </v-layout>
            </v-flex>
          </v-layout>
          <v-layout :class="{ 'mt-3': showLatLngMap }" v-if="isLocationTargeting && location" class="radius-selector">
            <v-flex xs3 md1>
              {{ $tc('message.bizgainOnboarding.radius') }}
            </v-flex>
            <v-flex xs9 md11>
              <v-radio-group
                class="pa-0 ma-0"
                hide-details
                v-model="selectedRadius"
                row
                @change="drawCircle"
              >
                <v-radio
                  v-for="(radius, index) in radiuses"
                  :key="index"
                  :class="{ 'mb-2': $vuetify.breakpoint.xsOnly }"
                  :label="$tc(radius.label)"
                  color="primary"
                  :value="radius.value"
                />
              </v-radio-group>
            </v-flex>
          </v-layout>
          <v-layout
            align-center
            justify-center
          >
            <v-flex
              v-if="isLoadingLocationTargeting || isLoadingRegionTargeting"
              xs12
              class="pa-4"
            >
              <v-layout
                align-center
                justify-center
              >
                <v-progress-circular
                  indeterminate
                  :size="70"
                  :width="4"
                  color="primary"
                />
              </v-layout>
            </v-flex>
          </v-layout>
        </v-container>
      </v-card>
    </div>
  </v-flex>
</template>
<script>
import {
  VFlex,
  VCard,
  VLayout,
  VRadioGroup,
  VRadio,
  VAutocomplete,
  VContainer,
  VChip,
  VIcon,
  VProgressCircular
} from 'vuetify/lib';
import MapLocation from 'Features/bizgainOnboarding/pages/locationTargeting/components/MapLocation.vue'
import { targetType, radiuses, distanceUnit, bizgainOnboardingRoutes, onboardingSections, subsequentFlowSteps } from 'Features/bizgainOnboarding/constants';
import { mapActions, mapGetters } from 'vuex';
import debounce from 'lodash/debounce';
import { ApiService } from 'Features/bizgainOnboarding/services/apiService';
import onboardingRedirect from 'Mixins/onboardingRedirect';
import { i18n } from '@/lang/';
const { getSearchLocation } = new ApiService();
export default {
  mixins: [onboardingRedirect],
  components: {
    VFlex,
    VCard,
    VLayout,
    VRadioGroup,
    VRadio,
    VAutocomplete,
    VContainer,
    VChip,
    VIcon,
    MapLocation,
    VProgressCircular
  },
  data() {
    return {
      targetingType: targetType.latlng,
      targetType,
      searchResults: [],
      search: null,
      location: null,
      selectedRadius: 10,
      radiuses,
      selectedGMapSpecificLocation: null,
      specificLocation: null,
      searchSpecificLocation: null,
      specificLocationResult: [],
      loadingSearchSpecificLocation: false,
      selectedSpecificLocations: [],
      specificLocationGMapSearch: null,
      showLatLngMap: false,
      showRegionMap: false,
      newBusinessDetails: null,
      newAdditionalConfig: null,
      distanceUnit: distanceUnit.kiloMeters,
      isLoadingTargeting: false,
      isPlaceSuggestionsLoading: false,
      bizgainOnboardingRoutes,
      isUpdateLocationTargeting: false,
      placeDetails: null
    }
  },
  computed: {
    ...mapGetters([
      'portal',
      'businessProfileId',
      'roimaticConfiguration',
      'roimaticCampaign',
      'roimaticCampaignId',
      'additionalConfiguration',
      'businessDetails',
      'isUpdateAddtionalConfig',
      'isErrorAddtioanlConfig',
      'isInAppCampaign',
      'region'
    ]),
    isLocationTargeting() {
      return this.targetingType == this.targetType.latlng;
    },
    isRegionTargeting() {
      return this.targetingType == this.targetType.region;
    },
    isLoadingLocationTargeting(){
      return this.isLocationTargeting && !this.location && this.businessDetails && this.businessDetails.targeting && this.businessDetails.targeting.location && this.businessDetails.targeting.location.radius > 0;
    },
    isLoadingRegionTargeting(){
      return this.selectedSpecificLocations.length === 0 &&
      this.businessDetails && this.businessDetails.targeting &&
      this.businessDetails.targeting.location && this.businessDetails.targeting.location.regions &&
      this.businessDetails.targeting.location.regions.length > 0;
    },
    buttonText(){
      if(this.$route.params.fromSummary){
        return i18n.tc('message.bizgainOnboarding.save');
      }
      return i18n.tc('message.bizgainOnboarding.next');
    }
  },
  async created() {
    this.isLoadingTargeting = true;
    if(this.isInAppCampaign &&
      (this.roimaticConfiguration
      && this.roimaticConfiguration.accounts
      && this.roimaticConfiguration.accounts.length > 0)
      && this.roimaticConfiguration.facebookPage
      && this.roimaticConfiguration.facebookPage.id
      && subsequentFlowSteps[onboardingSections.LAUNCH - 1].steps.length > 3){
      subsequentFlowSteps[onboardingSections.LAUNCH - 1].steps.splice(-1);
    }
    this.setSubsequentSteps(subsequentFlowSteps);
    this.$emit('updateIsLoadingTargeting', true);
    if (this.isInAppCampaign)
    {
      if(!this.roimaticCampaign) {
        let payload = {
          businessProfileId: this.businessProfileId,
          campaignId: this.roimaticCampaignId
        };
        await this.getRoimaticCampaign(payload);
      }
    }

    this.$nextTick(async () => {
      await this.$refs.latlngMap.mapsInit();
      await this.$refs.region.mapsInit();
      await this.setLocationTargeting();
      if (this.location && this.location.value) {
        await this.$refs.latlngMap.getPlaceDetails(this.location.value);
      }
    });
    this.isLoadingTargeting = false;
  },
  methods: {
    ...mapActions(['setSubsequentSteps', 'updateMessage', 'getRoimaticCampaign', 'patchAdditionalConfiguration', 'clearMessages', 'patchBusinessDetails']),
    async setLocationTargeting() {
      let campaignLocation = this.isInAppCampaign ?
        this.additionalConfiguration &&
        this.additionalConfiguration.localBusiness &&
        this.additionalConfiguration.localBusiness.targeting &&
        this.additionalConfiguration.localBusiness.targeting.location :
        this.businessDetails &&
        this.businessDetails.targeting &&
        this.businessDetails.targeting.location;
      if(campaignLocation)
      {
        if(campaignLocation.radius)
        {
          this.selectedRadius = campaignLocation.radius;
        }
        this.targetingType = campaignLocation.type;
        await this.setLocationFromCampaign(campaignLocation);
      }
      else
      {
        let bpLocation = this.isInAppCampaign ?
          this.roimaticConfiguration &&
          this.roimaticConfiguration.localBusinessConfiguration &&
          this.roimaticConfiguration.localBusinessConfiguration.googlePlaceDetail :
          this.businessDetails &&
          this.businessDetails.googlePlaceDetail;

        if(bpLocation)
        {
          if(bpLocation.placeId)
          {
            let location = {
              text: bpLocation.address,
              value: bpLocation.placeId,
              shortText: bpLocation.name
            };
            this.searchResults.push(location);
            this.location = location;
          }
        }
      }
    },
    async setLocationFromCampaign(location) {
      switch (location.type) {
        case this.targetType.latlng:
          this.targetingType = this.targetType.latlng;
          await this.$refs.latlngMap.getPlaceDetails(location.googlePlaceId);
          let selectedLocation = {
            text: this.$refs.latlngMap.placeDetails && this.$refs.latlngMap.placeDetails.address,
            value: location.googlePlaceId,
            shortText: location.googlePlaceName
          };
          this.searchResults.push(selectedLocation);
          this.location = selectedLocation;
          break;
        case this.targetType.region:
          this.targetingType = this.targetType.region;
          if (Array.isArray(location.regions) && location.regions.length > 0) {
            this.selectedSpecificLocations = location.regions.map(region => {
              return {
                shortText: region.name,
                text: region.completePath.lastIndexOf(',City') !== -1 ? region.completePath.substring(0, region.completePath.lastIndexOf(',City')) : region.completePath,
                value: region
              }
            });
          }
          break;
      }
      this.updateLocationTargetingDetails();
    },
    getMapHeader(showMap) {
      return showMap ? this.$tc('message.bizgainOnboarding.hideMap') : this.$tc('message.bizgainOnboarding.viewInMap');
    },
    setSearchResult(searchResults) {
      if (Array.isArray(searchResults) && searchResults.length > 0) {
        if (this.isLocationTargeting) {
          this.searchResults = searchResults;
        }
      }
    },
    getSelectedLocation() {
      let isPlaceSelected = this.selectedSpecificLocations.find(x => x.text == this.specificLocation.text);
      if (!isPlaceSelected) {
        this.selectedSpecificLocations.push(this.specificLocation);
        this.specificLocation = null;
        this.specificLocationResult = [];
      }
      this.updateLocationTargetingDetails();
    },
    getPlaceDetails() {
      if (this.$refs.latlngMap.marker) {
        this.$refs.latlngMap.removeMarker(this.$refs.latlngMap.marker);
      }
      if (this.location && this.location.value) {
        this.$refs.latlngMap.getPlaceDetails(this.location.value, true, this.selectedRadius);
      }
      this.updateLocationTargetingDetails();
    },
    drawCircle() {
      this.$refs.latlngMap.drawCircle(this.selectedRadius);
      this.updateLocationTargetingDetails();
    },
    removeSpecificLocation(index) {
      this.selectedSpecificLocations.splice(index, 1);
    },
    discardChanges(){
      this.selectedRadius = 10;
      this.location = null;
      this.targetingType = this.targetType.latlng;
    },
    setPlaceSuggestionsLoading(isLoading){
      this.isPlaceSuggestionsLoading = isLoading;
    },
    getSpecificSearchLocation: debounce(function(keyword) {
      try {
        this.specificLocationResult = [];
        this.clearMessages();
        let promise = getSearchLocation(keyword);
        promise.then((locations) => {
          if (locations && Array.isArray(locations) && locations.length > 0) {
            this.specificLocationResult = locations.sort((a, b) => {
              if (a.code === this.region && b.code !== this.region){
                return -1;
              }
              if (a.code !== this.region && b.code === this.region){
                return 1;
              }
              return a.code - b.code;
            }).map(location => {
              return {
                shortText: location.name,
                text: location.completePath.lastIndexOf(',City') !== -1 ? location.completePath.substring(0, location.completePath.lastIndexOf(',City')) : location.completePath,
                value: location
              }
            });
          }
        })
      } catch (error) {
        this.updateMessage({ type: 'error', message: this.$t('message.bizgainOnboarding.failedToFetchLocation') });
      } finally {
        this.loadingSearchSpecificLocation = false;
      }
    }, 400),
    updatePlaceDetails(values){
      this.placeDetails = values;
      this.updateLocationTargetingDetails();
    },
    async updateLocationTargetingDetails(values = null){
      if(values && values.selectedSpecificLocations && !values.selectedSpecificLocations.length){
        this.$emit('updateIsLoadingTargeting', true);
        return;
      }
      let isValid = await this.$validator.validate();
      let details = null;
      if(values){
        details = values;
      }else{
        details = {
          targetingType: this.targetingType,
          placeDetails: this.placeDetails,
          isValid: isValid,
          selectedRadius: this.selectedRadius,
          distanceUnit: this.distanceUnit,
          selectedSpecificLocations: this.selectedSpecificLocations,
          location: this.location
        }
      }
      this.$emit('updateIsLoadingTargeting', false);
      this.$emit('update', details);
    }
  },
  watch: {
    searchSpecificLocation(newValue) {
      if (newValue && (!this.specificLocation || (typeof(this.specificLocation) == 'object' && this.specificLocation.text != newValue))) {
        this.loadingSearchSpecificLocation = true;
        this.getSpecificSearchLocation(newValue.trim());
      }
    },
    async selectedSpecificLocations(newValue) {
      let isValid = await this.$validator.validate();
      let details = {
        targetingType: this.targetingType,
        placeDetails: this.placeDetails,
        isValid: isValid,
        selectedRadius: this.selectedRadius,
        distanceUnit: this.distanceUnit,
        selectedSpecificLocations: newValue,
        location: this.location
      }
      this.updateLocationTargetingDetails(details);
    },
    async location(newValue){
      let isValid = await this.$validator.validate();
      let details = {
        targetingType: this.targetingType,
        placeDetails: this.placeDetails,
        isValid: isValid,
        selectedRadius: this.selectedRadius,
        distanceUnit: this.distanceUnit,
        selectedSpecificLocations: newValue,
        location: newValue
      }
      this.updateLocationTargetingDetails(details);
    },
    async targetingType(newValue){
      let isValid = await this.$validator.validate();
      let details = {
        targetingType: newValue,
        placeDetails: this.placeDetails,
        isValid: isValid,
        selectedRadius: this.selectedRadius,
        distanceUnit: this.distanceUnit,
        selectedSpecificLocations: this.selectedSpecificLocations,
        location: newValue
      }
      this.updateLocationTargetingDetails(details);
    }
  }
}
</script>
<style lang="scss" scoped>
::v-deep.location-targeting {
  .v-input--radio-group {
    .v-label {
      color: var(--v-black-base);
    }
  }
  .v-card {
    .google-search {
      input {
        color: var(--v-black-base);
        line-height: 1.38;
      }
    }
    .selected-location-chips {
      background-color: var(--v-primary-variation-1-base);
      color: var(--v-black-base);
      max-height: 28px;
    }
    .location-text {
      color: var(--v-black-base);
      font-weight: 600;
      line-height: 1.33;
      font-size: 15px;
    }
    .map {
      width: 100%;
      height: 15rem;
    }
    .map-header {
      padding: 0;
      color: var(--v-primary-base);
      line-height: 1.38;
      text-decoration: underline;
      min-height: 1rem;
      padding-bottom: 0.5rem;
    }
    .radius-selector {
      .flex {
        font-size: 15px;
      }
      .v-input--radio-group {
        label {
          font-size: 14px;
        }
      }
    }
  }
}
::v-deep.text-ellipsis {
  text-align: left;
}
</style>